import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Grid2, Typography, useMediaQuery, useTheme } from "@mui/material";
import {
  CalendarNav,
  CalendarNext,
  CalendarPrev,
  CalendarToday,
  Eventcalendar,
  locale,
  MbscCalendarEvent,
  MbscCalendarEventData,
  MbscEventcalendarView,
  MbscEventClickEvent,
  MbscEventCreateEvent,
  MbscEventDragEvent,
  MbscEventUpdateEvent,
  MbscPageLoadedEvent,
  MbscPageLoadingEvent
} from "@mobiscroll/react";
import { DateTime } from "luxon";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "../../../app/helpers";
import { RootState } from "../../../app/rootReducer";
import { TeamMember } from "../../../features/Booking-Form/typings/team-member";
import BookingSvgDialog from "../../Svg/svg-dialog.component";
import { EVENT_CALENDAR_GER } from "../schedule-calendar-localization";
import { ScheduleEventContent } from "../schedule-event-content.component";
import { ScheduleUnlockDialog } from "./ScheduleUnlockDialog/ScheduleUnlockDialog";
import {
  checkInvalidCreateOnSchedule,
  compareOldEventTime,
  updateCalendarDate,
  updateTimes
} from "../functions/schedule.functions";
import {
  BookingScheduleInterface,
  RestrictionZone
} from "../../../features/Booking-Form/typings/booking.types";
import { BookingType } from "../../../features/Booking-Form/typings/booking-inputs";
import { useRemoteUnlockLock } from "../../../hooks/Remote/Unlockable/useRemoteUnlockLock";
import { BulkUnlockable } from "./ScheduleUnlockDialog/types";
import {
  checkIsFastUnlock,
  handleClickFastUnlock
} from "./ScheduleUnlockDialog/functions/schedule-unlock.functions";
import { handleClickFastCheckin } from "./ScheduleCheckinDialog/functions/schedule-checkin.functions";
import { ScheduleCheckinDialog } from "./ScheduleCheckinDialog/ScheduleCheckinDialog";
import { useRemoteCheckin } from "../../../hooks/Remote/Checkin/useRemoteCheckin";
import { attemptCheckin, handleCheckinResponse } from "../../../features/Checkin/functions/checkin";
import { ScheduleSelectionDialog } from "./ScheduleSelectionDialog/ScheduleSelectionDialog.component";
import { ScheduleCreateOnCalendarDialog } from "./ScheduleCreateOnCalendarDialog/ScheduleCreateOnCalendarDialog.component";
import {
  clearBookingFilters,
  clearFloors,
  clearInputs,
  setInputs
} from "../../../features/Booking-Form/slices/booking.slice";
import { ScheduleCalendarTopToolbar } from "./partial/ScheduleCalendarTopToolbar.partial";
import { generateTodayColor } from "./functions/generateScheduleColor.functions";
import { generateHolidaySchedule } from "./functions/generateHolidaySchedule.function";
import {
  generateInactivitySchedule,
  type InactivityOverlayEvent
} from "./functions/generateInactivitySchedule.function";
import {
  isHolidayEventId,
  isInactivityEventId,
  resolveInfoEventSelection
} from "./functions/resolveInfoEventSelection.function";
import { getHolidayLabelTextColor } from "./functions/schedule-calendar-colors";
import { filterTodaysSchedule } from "./functions/filterTodaysSchedule.function";
import { filterUnlockableSchedule } from "./functions/filterUnlockableSchedule.function";
import { useRemoteFetchZoneInventory } from "../../../hooks/Remote/Inventory/useRemoteFetchZoneInventoryMutation";
import { setPicturesFromSchedule } from "./functions/setPicturesFromSchedule.function";
import { sortSchedulesByLastName } from "./functions/sortSchedulesByLastName.function";
import { compareEventsByScheduleRenderOrder } from "./functions/compareEventsByScheduleRenderOrder.function";
import { ScheduleMode } from "../../../features/Schedule/partial/ScheduleModeToggle.partial";
import {
  ScheduleResourceBookDialog,
  type ScheduleResourceBookCalendarPrefetch
} from "../../../features/Schedule/partial/ScheduleResourceBookDialog.partial";
import type { ScheduleResourceAutocompleteOption } from "../../../features/Schedule/partial/ScheduleResourceAutocomplete.partial";
import { ScheduleHolidayInfoPopper } from "./partial/ScheduleHolidayInfoPopper.partial";
import { ScheduleInactivityInfoPopper } from "./partial/ScheduleInactivityInfoPopper.partial";
import type { ResourceInactivityResponseDto } from "../../../features/Schedule/typings/resource-calendar.types";
import type { RefetchAllScheduleFn } from "../../../features/Schedule/typings/refetch-schedule.types";
import type { ScheduleExcelExportFetchConfig } from "./functions/schedule-excel-export-fetch.functions";
import { canBookInactiveResourcesAsAdmin } from "../../BookingForm/functions/canBookInactiveResourcesAsAdmin.function";
import { useLocationAdminLocationIds } from "../../../hooks/Remote/User/UserInfo/useLocationAdminLocationIds";

type P = {
  monthView?: boolean;
  users?: TeamMember[];
  selectedRestriction?: RestrictionZone;
  selectedColleagues?: TeamMember[];
  deletingBookings?: boolean;
  setDeletingBookings?: (d: boolean) => void;
  selectedZone?: string;
  setSelectedAppointment?: (selectedAppointment: number) => void;
  selectedAppointments?: number[];
  schedules: BookingScheduleInterface[];
  scheduleDataStatus: "error" | "loading" | "success" | "idle";
  refetchAllSchedule: RefetchAllScheduleFn;
  setCalendarDate: React.Dispatch<
    React.SetStateAction<{ firstDay: string | undefined; lastDay: string | undefined }>
  >;
  isFirstAidSelected: boolean;
  setIsFirstAidSelected: () => void;
  /** When provided (including `null`), this is the resource-mode calendar: create gestures open the resource book dialog instead of colleague booking. */
  selectedResource?: ScheduleResourceAutocompleteOption | null;
  /** Parent schedules for the visible calendar range; lets the resource book dialog skip a duplicate `by-booking-object` when the clicked day is inside this range. */
  resourceBookCalendarPrefetch?: ScheduleResourceBookCalendarPrefetch | null;
  /** Resource-mode inactivity intervals rendered as informational overlay events. */
  resourceInactivity?: ResourceInactivityResponseDto | null;
  /** Active resource calendar booking type filter (resource mode). */
  resourceBookingType?: BookingType | null;
  scheduleMode: ScheduleMode;
  /** Visible calendar range for prefilling the export dialog period. */
  excelExportInitialRange?: { startDate: string; endDate: string } | null;
  excelExportFetchConfig?: ScheduleExcelExportFetchConfig;
  calendarHeight?: number;
};

function defaultExcelExportFetchConfig(
  scheduleMode: ScheduleMode,
  userIds: string[]
): ScheduleExcelExportFetchConfig {
  if (scheduleMode === ScheduleMode.Colleague) {
    return {
      scheduleMode: ScheduleMode.Colleague,
      userIds,
      workplaceBookingEnabled: false,
      hasConferenceLicense: false,
      hasVehicleLicense: false
    };
  }

  return {
    scheduleMode: ScheduleMode.Resource,
    resourceBookingType: null,
    resourceAvailableTypes: []
  };
}

const calendarWeekView: MbscEventcalendarView = {
  schedule: {
    type: "week",
    /** Specifies the first visible weekday of the view. Sunday is 0, Monday is 1, etc */
    startDay: 1,
    /** Specifies the last visible weekday of the view: Sunday is 0, Monday is 1, etc */
    endDay: 5
  }
};
const calendarMonthView: MbscEventcalendarView = {
  calendar: {
    type: "month"
  }
};

const ScheduleCalendar: React.FC<P> = ({
  users,
  monthView,
  setSelectedAppointment,
  selectedAppointments,
  selectedColleagues,
  deletingBookings,
  setDeletingBookings,
  schedules,
  scheduleDataStatus,
  refetchAllSchedule,
  setCalendarDate,
  isFirstAidSelected,
  setIsFirstAidSelected,
  selectedResource,
  resourceBookCalendarPrefetch,
  resourceInactivity,
  resourceBookingType = null,
  scheduleMode,
  excelExportInitialRange = null,
  excelExportFetchConfig: excelExportFetchConfigProp,
  calendarHeight
}) => {
  const excelExportFetchConfig =
    excelExportFetchConfigProp ??
    defaultExcelExportFetchConfig(
      scheduleMode,
      (users ?? []).map(user => user.userId)
    );

  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { t, i18n } = useTranslation();
  const {
    settings: { language, timezone },
    userInformation: {
      sub,
      company: {
        meta: {
          checkinSettings,
          bookingsOnSaturdaysAllowed,
          bookingsOnSundaysAllowed,
          activateOfficeSafety
        }
      }
    }
  } = useSelector((state: RootState) => state.login);
  const { isCompanyAdmin, isLocationAdmin } = useSelector(
    (state: RootState) => state.login.userRoles
  );
  const locationAdminLocationIds = useLocationAdminLocationIds();
  const dispatch = useDispatch();
  const isBrowser = useMediaQuery(theme.breakpoints.up(1400));

  const [detailPanelOpen, setDetailPanelOpen] = useState(false);
  const [targetScheduleId, setTargetScheduleId] = useState<number>();
  const [targetPictures, setTargetPictures] = useState<string[]>([]);
  const [targetHolidayId, setTargetHolidayId] = useState<string>("");
  const [targetInactivityId, setTargetInactivityId] = useState<string>("");
  const [selectedSchedule, setSelectedSchedule] = useState<BookingScheduleInterface>();
  const [isShowFloorDialogOpen, setIsShowFloorDialogOpen] = useState<boolean>(false);

  // Calendar
  const [calendarData, setCalendarData] = useState<MbscCalendarEvent[]>([]);
  const initialView = monthView ? "month" : "week";
  const [currentView, setCurrentView] = useState<"week" | "month">(initialView);
  const [currentLoadedEvent, setCurrentLoadedEvent] = useState<MbscPageLoadedEvent>(); // MbscPageLoadingEvent
  const initialCalView = currentView === "month" ? calendarMonthView : calendarWeekView;
  const [calView, setCalView] = useState<MbscEventcalendarView>(initialCalView);

  // Modal of Unlockable for selected unlockable schedule
  const [isUnlockModalOpen, setIsUnlockModalOpen] = useState<boolean>(false);
  // Modal of Unlockable for bulk unlockable schedule
  const [isBulkUnlockModalOpen, setIsBulkUnlockModalOpen] = useState<boolean>(false);

  // Fast Checkin for today schedule
  const [todaysCheckin, setTodaysCheckin] = useState<BookingScheduleInterface[] | undefined>(
    undefined
  );
  // Modal of checkin for today schedules
  const [isCheckinModalOpen, setIsCheckinModalOpen] = useState(false);

  // Fast Unlockable for schedule
  const [unlockableSchedules, setUnlockableSchedules] = useState<
    BookingScheduleInterface[] | undefined
  >(undefined);
  // Fast Unlockable data
  const [bulkUnlockData, setBulkUnlockData] = useState<BulkUnlockable[] | undefined>(undefined);

  // booking CreateOnCalendar Modal open
  const [isCreateOnCalendar, setIsCreateOnCalendar] = useState<{
    start: DateTime | null;
    end: DateTime | null;
    open: boolean;
  }>({ start: null, end: null, open: false });

  const isResourceCalendar = selectedResource !== undefined;
  const [resourceBookDialogOpen, setResourceBookDialogOpen] = useState(false);
  const [resourceBookDialogRange, setResourceBookDialogRange] = useState<{
    end: string;
    start: string;
  } | null>(null);

  const targetSchedule = useMemo(() => {
    return schedules.find(s => s.id === targetScheduleId);
  }, [schedules, targetScheduleId]);

  const { sortedSchedulesByLastName, scheduleRenderOrderById } = useMemo(() => {
    const sorted = sortSchedulesByLastName({ schedules, users });
    return {
      sortedSchedulesByLastName: sorted,
      scheduleRenderOrderById: new Map(sorted.map((schedule, index) => [schedule.id, index]))
    };
  }, [schedules, users]);

  const eventOrder = useCallback(
    (event1: MbscCalendarEvent, event2: MbscCalendarEvent) =>
      compareEventsByScheduleRenderOrder({ event1, event2, scheduleRenderOrderById }),
    [scheduleRenderOrderById]
  );

  /**
   * calendar colors generating from today and holidays
   */
  const calendarColors = useMemo(() => generateTodayColor({ theme }), [theme.palette.mode]);

  /**
   * calculating holiday schedules within the month user sees
   */
  const holidayScheduleData = useMemo(
    () => generateHolidaySchedule({ currentLoadedEvent, timezone, theme, language: i18n.language }),
    [theme.palette.mode, currentLoadedEvent, i18n.language, timezone]
  );
  const targetHolidaySchedule = useMemo(() => {
    return holidayScheduleData.find(s => String(s.id) === targetHolidayId);
  }, [holidayScheduleData, targetHolidayId]);

  /**
   * calculating inactivity schedules within the month user sees
   */
  const inactivityScheduleData = useMemo(
    (): InactivityOverlayEvent[] =>
      generateInactivitySchedule({ language: i18n.language, resourceInactivity, timezone }),
    [i18n.language, resourceInactivity, timezone]
  );
  const targetInactivitySchedule = useMemo(
    () => inactivityScheduleData.find(s => s.id === targetInactivityId),
    [inactivityScheduleData, targetInactivityId]
  );

  const { isFastUnlock, isFastUnlockDoorName } = checkIsFastUnlock(unlockableSchedules);

  const { mutate: unlock, status: unlockStatus } = useRemoteUnlockLock(
    isFastUnlock,
    isFastUnlockDoorName as string
  );

  const {
    mutate: mutateCheckin,
    error: checkinError,
    status: checkinStatus,
    data: checkinRes
  } = useRemoteCheckin();

  const { mutateAsync: fetchZoneInventory, isLoading: fetchZoneInventoryLoading } =
    useRemoteFetchZoneInventory();

  const resetResourceBookBookingRedux = useCallback(() => {
    dispatch(clearInputs());
    dispatch(clearFloors());
    dispatch(clearBookingFilters());
  }, [dispatch]);

  const handleResourceBookDialogClose = useCallback(() => {
    resetResourceBookBookingRedux();
    setResourceBookDialogOpen(false);
    refetchAllSchedule({ force: true });
  }, [refetchAllSchedule, resetResourceBookBookingRedux]);

  useEffect(() => {
    if (!isResourceCalendar) return;
    if (!selectedResource) {
      setResourceBookDialogOpen(false);
      setResourceBookDialogRange(null);
    }
  }, [isResourceCalendar, selectedResource]);

  // Update Calendar data on view changes
  const refetchData = useCallback(() => {
    updateCalendarDate(currentLoadedEvent, setCalendarDate, refetchAllSchedule);
  }, [currentLoadedEvent, refetchAllSchedule, setCalendarDate]);

  const pageLoadingEventRef = useRef<MbscPageLoadingEvent | null>(null);
  const isRenderingRef = useRef(false);
  isRenderingRef.current = true;

  const onPageLoading = useCallback(
    (event: MbscPageLoadingEvent) => {
      if (isRenderingRef.current) {
        pageLoadingEventRef.current = event;
      } else {
        updateCalendarDate(event, setCalendarDate);
      }
    },
    [setCalendarDate]
  );

  const handlePageLoaded = useCallback((event: MbscPageLoadedEvent) => {
    setCurrentLoadedEvent(prev => {
      const prevFirst = prev?.firstDay?.toISOString();
      const prevLast = prev?.lastDay?.toISOString();
      const nextFirst = event.firstDay.toISOString();
      const nextLast = event.lastDay.toISOString();
      return prevFirst === nextFirst && prevLast === nextLast ? prev : event;
    });
  }, []);

  useEffect(() => {
    isRenderingRef.current = false;
    if (pageLoadingEventRef.current) {
      updateCalendarDate(pageLoadingEventRef.current, setCalendarDate);
      pageLoadingEventRef.current = null;
    }
  });

  const handleClickUnlock = useCallback(() => {
    setIsUnlockModalOpen(true);
  }, []);

  const openResourceBookDialogIfApplicable = useCallback(
    (bookingStartDate: DateTime, bookingEndDate: DateTime) => {
      if (!isResourceCalendar) return false;
      if (!selectedResource) return true;

      setResourceBookDialogRange({
        start: bookingStartDate.toFormat("yyyy-MM-dd"),
        end: bookingEndDate.toFormat("yyyy-MM-dd")
      });
      setResourceBookDialogOpen(true);
      return true;
    },
    [isResourceCalendar, selectedResource]
  );

  const skipInactiveOverlapForAdmin = useMemo(
    () =>
      canBookInactiveResourcesAsAdmin({
        isCompanyAdmin,
        isLocationAdmin,
        bookingType: resourceBookingType,
        locationInventoryId: selectedResource?.locationInventoryId,
        locationAdminLocationIds
      }),
    [
      isCompanyAdmin,
      isLocationAdmin,
      resourceBookingType,
      selectedResource?.locationInventoryId,
      locationAdminLocationIds
    ]
  );

  const hasInactiveOverlapForResourceCreate = useCallback(
    (bookingStartDate: DateTime, bookingEndDate: DateTime): boolean => {
      if (!isResourceCalendar || !selectedResource) {
        return false;
      }
      if (skipInactiveOverlapForAdmin) {
        return false;
      }
      if (inactivityScheduleData.length === 0) {
        return false;
      }

      const selectionStart = bookingStartDate;
      const selectionEnd =
        bookingEndDate <= bookingStartDate ? bookingStartDate.plus({ minutes: 1 }) : bookingEndDate;

      return inactivityScheduleData.some(inactive => {
        const inactiveStart = DateTime.fromISO(inactive.start).setZone(timezone);
        const inactiveEnd = DateTime.fromISO(inactive.end).setZone(timezone);
        if (!inactiveStart.isValid || !inactiveEnd.isValid) {
          return false;
        }
        return inactiveStart < selectionEnd && inactiveEnd > selectionStart;
      });
    },
    [
      inactivityScheduleData,
      isResourceCalendar,
      selectedResource,
      skipInactiveOverlapForAdmin,
      timezone
    ]
  );

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  /* istanbul ignore next */
  const handleClickInfoEvent = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      // prohibit popper when browser
      // if (isBrowser) return;
      setAnchorEl(prev => (prev ? null : event.currentTarget));
    },
    [isBrowser]
  );

  const infoPopperOpen = Boolean(anchorEl);
  /* istanbul ignore next */
  const infoPopperId = infoPopperOpen ? "simple-popper" : undefined;

  const getCalendarEventContent = useCallback(
    (event: MbscCalendarEventData) => {
      const scheduleFound = schedules.find(s => s.id === event.id);

      // mostly, holiday schedule or inactivity schedule
      if (!scheduleFound) {
        const eventId = String(event.id);
        const labelTextColor = isHolidayEventId(eventId)
          ? getHolidayLabelTextColor(theme)
          : theme.palette.text.primary;

        return (
          <Grid2
            onClick={clickEvent => /** istanbul ignore next */ {
              const selection = resolveInfoEventSelection(eventId);
              setTargetHolidayId(selection.targetHolidayId);
              setTargetInactivityId(selection.targetInactivityId);
              handleClickInfoEvent(clickEvent);
            }}
            data-testid={`calendar-info-content-${eventId}`}
          >
            <Typography variant={"inherit"} sx={{ color: labelTextColor, cursor: "default" }}>
              {event.title}
            </Typography>
          </Grid2>
        );
      }

      return (
        <ScheduleEventContent
          event={event}
          monthView={currentView === "month"}
          schedule={scheduleFound}
          selectedColleagues={selectedColleagues}
          users={users}
          selectedAppointments={selectedAppointments}
          setSelectedAppointment={setSelectedAppointment}
          scheduleMode={scheduleMode}
        />
      );
    },
    [
      currentView,
      handleClickInfoEvent,
      scheduleMode,
      schedules,
      selectedAppointments,
      selectedColleagues,
      setSelectedAppointment,
      theme.palette.text.primary,
      theme.palette.mode,
      users
    ]
  );

  const onEventUpdate = useCallback(
    async (event: MbscEventUpdateEvent) => {
      // compare the old event time and new event time and check allowing to trigger updateTimes
      if (
        !compareOldEventTime(
          event,
          calendarData,
          schedules.filter(item => item.userBookedFor === sub),
          setCalendarData,
          enqueueSnackbar
        )
      )
        return;

      await updateTimes(
        {
          bookingId: Number(event.event.id),
          newStartTime:
            DateTime.fromJSDate(new Date(event.event.start as string)).toISO() ||
            DateTime.now().toISO(),
          newEndTime:
            DateTime.fromJSDate(new Date(event.event.end as string)).toISO() ||
            DateTime.now().toISO(),
          timezone,
          event: event
        },
        schedules.find(s => s.id === event.event.id),
        calendarData,
        { enqueueSnackbar, setCalendarData, refetchAllSchedule }
      );
    },
    [calendarData, enqueueSnackbar, refetchAllSchedule, schedules, sub, timezone]
  );

  /* istanbul ignore next */
  const onEventClick = useCallback(
    async (event: MbscEventClickEvent) => {
      if (event.event.id === undefined) return;

      const eventId = event.event.id;

      // Holiday or Inactivity
      if (Number.isNaN(Number(eventId))) {
        const eventIdAsString = String(eventId);
        if (!isBrowser) {
          const infoTarget = (event.domEvent?.currentTarget ??
            event.domEvent?.target) as HTMLElement | null;
          if (infoTarget) {
            setAnchorEl(infoTarget);
          }
        }
        if (isInactivityEventId(eventIdAsString)) {
          setTargetInactivityId(eventIdAsString);
          setTargetHolidayId("");
          return;
        }

        const selection = resolveInfoEventSelection(eventIdAsString);
        setTargetHolidayId(selection.targetHolidayId);
        setTargetInactivityId("");
        return;
      }

      // Schedule
      const scheduleId = Number(eventId);
      setTargetScheduleId(scheduleId);

      const findSchedule = schedules.find(s => s.id === scheduleId);

      await setPicturesFromSchedule({
        schedule: findSchedule,
        fetchZoneInventory,
        setTargetPictures
      });

      // Always open after logic completes.
      setDetailPanelOpen(true);
    },
    [fetchZoneInventory, isBrowser, schedules]
  );

  const handleCreateByDragEnd = useCallback(
    (args: MbscEventDragEvent) => {
      /**
       * if new event do things below
       * if not(existing event) updateTimes by new event time
       */
      if (args.event.title !== "New event") return;

      const bookingStartDate = DateTime.fromJSDate(args.event.start as Date);
      const bookingEndDate = DateTime.fromJSDate(args.event.end as Date);

      const isInvalid = checkInvalidCreateOnSchedule(
        bookingStartDate,
        bookingsOnSaturdaysAllowed,
        bookingsOnSundaysAllowed,
        enqueueSnackbar,
        t
      );

      if (isInvalid) return;

      if (hasInactiveOverlapForResourceCreate(bookingStartDate, bookingEndDate)) {
        enqueueSnackbar(t("_scheduleResourceBook_inactiveOverlap"), { variant: "warning" });
        return;
      }

      if (openResourceBookDialogIfApplicable(bookingStartDate, bookingEndDate)) return;

      setIsCreateOnCalendar({ start: bookingStartDate, end: bookingEndDate, open: true });
      dispatch(
        setInputs({
          bookingFrom: bookingStartDate.toFormat("yyyy-MM-dd"),
          bookingTo: bookingEndDate.toFormat("yyyy-MM-dd")
        })
      );
    },
    [
      bookingsOnSaturdaysAllowed,
      bookingsOnSundaysAllowed,
      dispatch,
      enqueueSnackbar,
      hasInactiveOverlapForResourceCreate,
      openResourceBookDialogIfApplicable,
      t
    ]
  );

  // handle create on calendar event
  const handleCreateOnCalendar = useCallback(
    (args: MbscEventCreateEvent) => {
      if (args.action !== "click") return;
      const bookingStartDate = DateTime.fromJSDate(args.event.start as Date);

      const isInvalid = checkInvalidCreateOnSchedule(
        bookingStartDate,
        bookingsOnSaturdaysAllowed,
        bookingsOnSundaysAllowed,
        enqueueSnackbar,
        t
      );

      if (isInvalid) return;

      if (hasInactiveOverlapForResourceCreate(bookingStartDate, bookingStartDate)) {
        enqueueSnackbar(t("_scheduleResourceBook_inactiveOverlap"), {
          variant: "warning"
        });
        return;
      }

      if (openResourceBookDialogIfApplicable(bookingStartDate, bookingStartDate)) return;

      setIsCreateOnCalendar({ start: bookingStartDate, end: bookingStartDate, open: true });
      dispatch(
        setInputs({
          bookingFrom: bookingStartDate.toFormat("yyyy-MM-dd"),
          bookingTo: bookingStartDate.toFormat("yyyy-MM-dd")
        })
      );
    },
    [
      bookingsOnSaturdaysAllowed,
      bookingsOnSundaysAllowed,
      dispatch,
      enqueueSnackbar,
      hasInactiveOverlapForResourceCreate,
      openResourceBookDialogIfApplicable,
      t
    ]
  );

  const handleSingleUnlockableScheduleCheckin = useCallback(
    (bookingId: number, bookingType: BookingType) => {
      attemptCheckin(bookingId, bookingType, checkinSettings, mutateCheckin, {
        t,
        enqueueSnackbar
      });
    },
    [checkinSettings, enqueueSnackbar, mutateCheckin, t]
  );

  const handleShowFloor = useCallback((schedule: BookingScheduleInterface) => {
    setSelectedSchedule(schedule);
    setIsShowFloorDialogOpen(true);
  }, []);

  const handleShowFloorCloseFn = useCallback(() => {
    setSelectedSchedule(undefined);
    setIsShowFloorDialogOpen(false);
  }, []);

  useEffect(() => {
    // Update calendar view
    setCalView(initialCalView);
  }, [initialCalView]);

  useEffect(() => {
    if (deletingBookings !== undefined && !deletingBookings) {
      refetchData();
    }
  }, [deletingBookings, refetchData]);

  useEffect(() => {
    refetchData();
  }, [refetchData, users]);

  useEffect(() => {
    if (!schedules.length) {
      setTodaysCheckin(undefined);
      setCalendarData([...holidayScheduleData, ...inactivityScheduleData]);
      setUnlockableSchedules(undefined);
      return;
    }

    // wait for all types of data fetching is completed
    if (scheduleDataStatus === "loading" || scheduleDataStatus === "error") return;

    const schedulesConst = schedules.map(schedule => {
      return {
        id: schedule.id,
        title:
          schedule.locationName === "Mobile Working"
            ? t("Mobile Working")
            : `${schedule.locationName} ${" - "} ${schedule.floorName}`,
        start:
          DateTime.fromISO(schedule.startDate).setZone(timezone).toISO() || DateTime.now().toISO(),
        end: DateTime.fromISO(schedule.endDate).setZone(timezone).toISO() || DateTime.now().toISO(),
        color: schedule.color || theme.palette.text.secondary,
        editable: !schedule.isGuest
      };
    });

    setCalendarData([...schedulesConst, ...holidayScheduleData, ...inactivityScheduleData]);

    // filter todays schedule which requires check-in
    filterTodaysSchedule({ schedules, setTodaysCheckin });

    // check if schedules have doorLock data then show the fast lock icon
    filterUnlockableSchedule({ schedules, setUnlockableSchedules });
  }, [
    schedules,
    scheduleDataStatus,
    checkinStatus,
    unlockStatus,
    i18n.language,
    holidayScheduleData,
    inactivityScheduleData,
    sortedSchedulesByLastName,
    t,
    theme.palette.text.secondary,
    timezone
  ]);

  useEffect(() => {
    const checkinSetting = checkinSettings.find(
      s => todaysCheckin && todaysCheckin[0].bookingType === s.bookingType
    );

    handleCheckinResponse(
      {
        checkinRes,
        checkinError,
        errAvailabilityTimeOnTheFly: null,
        isCheckinPerBookingType: Boolean(checkinSetting),
        geoLocationRequired: Boolean(checkinSetting?.geoLocationRequired),
        checkinOnTheFly: false
      },
      { t, enqueueSnackbar, refetchAllSchedule, setShowDialog: setIsCheckinModalOpen }
    );
  }, [checkinError, checkinStatus, checkinRes]);

  const handleToolbarMultiCheckinClick = useCallback(() => {
    if (!todaysCheckin) return;

    handleClickFastCheckin(todaysCheckin, checkinSettings, setIsCheckinModalOpen, mutateCheckin, {
      t,
      enqueueSnackbar
    });
  }, [todaysCheckin, checkinSettings, mutateCheckin, t, enqueueSnackbar]);

  const handleToolbarBulkUnlockClick = useCallback(() => {
    if (!unlockableSchedules) return;

    handleClickFastUnlock(
      checkinSettings,
      unlockableSchedules,
      unlock,
      handleSingleUnlockableScheduleCheckin,
      setIsBulkUnlockModalOpen,
      setBulkUnlockData
    );
  }, [checkinSettings, unlockableSchedules, unlock]);

  const customWithNavButtons = useCallback(
    () => (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          width: "100%",
          gap: 1,
          px: 1,
          py: 0.5
        }}
        data-testid="schedule-calendar-header"
      >
        <ScheduleCalendarTopToolbar
          currentView={currentView}
          setCurrentView={setCurrentView}
          activateOfficeSafety={activateOfficeSafety}
          isFirstAidSelected={isFirstAidSelected}
          setIsFirstAidSelected={setIsFirstAidSelected}
          scheduleDataStatus={scheduleDataStatus}
          todaysCheckin={todaysCheckin}
          onMultiCheckinClick={handleToolbarMultiCheckinClick}
          unlockableSchedules={unlockableSchedules}
          onBulkUnlockClick={handleToolbarBulkUnlockClick}
          users={users}
          refetchAllSchedule={refetchAllSchedule}
          showColleagueToolbarActions={scheduleMode === ScheduleMode.Colleague}
          excelExportInitialRange={excelExportInitialRange}
          excelExportFetchConfig={excelExportFetchConfig}
        />

        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, flex: 1 }}>
            <CalendarNav />
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
            <CalendarPrev />
            <CalendarToday />
            <CalendarNext />
          </Box>
        </Box>
      </Box>
    ),
    [
      currentView,
      setCurrentView,
      activateOfficeSafety,
      isFirstAidSelected,
      setIsFirstAidSelected,
      scheduleDataStatus,
      todaysCheckin,
      handleToolbarMultiCheckinClick,
      unlockableSchedules,
      handleToolbarBulkUnlockClick,
      schedules,
      users,
      refetchAllSchedule,
      scheduleMode,
      excelExportInitialRange,
      excelExportFetchConfig
    ]
  );

  return (
    <>
      {/* schedule calendar */}
      <Eventcalendar
        height={calendarHeight ?? 900}
        firstDay={1}
        onEventClick={onEventClick}
        view={calView}
        data={calendarData}
        onPageLoaded={handlePageLoaded} // MbscPageLoadedEvent
        onPageLoading={onPageLoading} // MbscPageLoadingEvent
        timeFormat="H:mm"
        dragToResize={!isResourceCalendar && currentView === "week"}
        dragToCreate={true}
        dragToMove={!isResourceCalendar && currentView === "week"}
        clickToCreate={"single"}
        onEventCreate={handleCreateOnCalendar}
        onEventDragEnd={handleCreateByDragEnd}
        onEventUpdate={onEventUpdate}
        renderScheduleEventContent={getCalendarEventContent}
        renderLabelContent={getCalendarEventContent}
        renderEventContent={getCalendarEventContent}
        renderHeader={customWithNavButtons}
        locale={locale[i18n.language]}
        colors={calendarColors}
        eventOrder={eventOrder}
        {...(language === "de" ? EVENT_CALENDAR_GER : [])}
      />

      {/* slideout dialog describing the selected schedule */}
      {targetSchedule && (
        <ScheduleSelectionDialog
          scheduleMode={scheduleMode}
          isCalendarView={true}
          isEditable={scheduleMode === ScheduleMode.Colleague}
          isOpen={detailPanelOpen}
          schedule={targetSchedule}
          schedulePictures={targetPictures}
          pictureIsLoading={fetchZoneInventoryLoading}
          allSchedules={schedules}
          calendarData={calendarData}
          isFirstAidSelected={isFirstAidSelected}
          onClose={() => setDetailPanelOpen(false)}
          onCalendarDataChange={setCalendarData}
          onScheduleChange={handleShowFloor}
          onClickUnlock={handleClickUnlock}
          refetchAllSchedule={refetchAllSchedule}
          setDeletingBookings={setDeletingBookings}
          setCalendarData={setCalendarData}
        />
      )}

      {/* istanbul ignore next */}
      {targetHolidaySchedule && (
        <ScheduleHolidayInfoPopper
          popperId={infoPopperId}
          popperOpen={infoPopperOpen}
          anchorEl={anchorEl}
          holidaySchedule={targetHolidaySchedule}
          onClose={() => setAnchorEl(null)}
        />
      )}

      {/* istanbul ignore next */}
      {targetInactivitySchedule && (
        <ScheduleInactivityInfoPopper
          popperId={infoPopperId}
          popperOpen={infoPopperOpen}
          anchorEl={anchorEl}
          timezone={timezone}
          inactivitySchedule={targetInactivitySchedule}
          onClose={() => setAnchorEl(null)}
        />
      )}

      {/** handles todays checkin schedule */}
      {isCheckinModalOpen && (
        <ScheduleCheckinDialog
          isOpen={isCheckinModalOpen}
          onClose={() => setIsCheckinModalOpen(false)}
          checkinSchedules={todaysCheckin}
          refetchAllSchedule={refetchAllSchedule}
        />
      )}

      {/** handles locking and unlocking doors on site */}
      {targetSchedule && isUnlockModalOpen && targetSchedule.doorLockData && (
        <ScheduleUnlockDialog
          unlockableData={targetSchedule.doorLockData}
          open={isUnlockModalOpen}
          onClose={() => setIsUnlockModalOpen(false)}
          title={"target"}
          bookingId={targetSchedule?.id}
          bookingType={targetSchedule.bookingType}
          checkedIn={targetSchedule.checkedIn}
          unCheckedinSchedules={undefined}
          refetchAllSchedule={refetchAllSchedule}
        />
      )}

      {/** handles unlocking bulk doors from accumulated bookings instantly */}
      {bulkUnlockData && bulkUnlockData?.length > 0 && isBulkUnlockModalOpen && (
        <ScheduleUnlockDialog
          open={isBulkUnlockModalOpen}
          onClose={() => setIsBulkUnlockModalOpen(false)}
          unlockableData={[]}
          bulkUnlockData={bulkUnlockData}
          title={"bulk"}
          bookingId={bulkUnlockData[0].bookingId}
          bookingType={bulkUnlockData[0].bookingType}
          checkedIn={unlockableSchedules?.[0]?.checkedIn || false}
          unCheckedinSchedules={unlockableSchedules?.find(s => !s.checkedIn)}
          refetchAllSchedule={refetchAllSchedule}
        />
      )}

      {/* handle create on calendar dialog - colleague mode only */}
      {!isResourceCalendar && isCreateOnCalendar.open && (
        <ScheduleCreateOnCalendarDialog
          open={isCreateOnCalendar.open}
          isCreateOnCalendar={isCreateOnCalendar}
          onClose={() => {
            setIsCreateOnCalendar({ start: null, end: null, open: false });
            refetchAllSchedule();
          }}
        />
      )}

      {/* dialog to display where the selected schedule on the floor plan */}
      {selectedSchedule && (
        <BookingSvgDialog
          isShowDialogOpen={isShowFloorDialogOpen}
          bookingId={selectedSchedule.id}
          bookingType={selectedSchedule.bookingType as BookingType}
          highlight={selectedSchedule.bookingInventoryId}
          closeFn={handleShowFloorCloseFn}
          labelButton={t("Close")}
        />
      )}

      {selectedResource && (
        <ScheduleResourceBookDialog
          calendarPrefetch={resourceBookCalendarPrefetch ?? null}
          initialPageRange={resourceBookDialogRange}
          open={resourceBookDialogOpen}
          row={selectedResource}
          onClose={handleResourceBookDialogClose}
        />
      )}
    </>
  );
};

export default React.memo(ScheduleCalendar);
